// Space weather in the viewer: the live aurora on the Earth and the Sun as
// it is today, while the clock is at or near the times the data covers.
//
// The aurora (earthAuroraFragmentShader, src/shaders/aurora.js): while the
// simulation's moment falls inside NOAA's Kp series (a week observed, three
// days forecast), Kp sets the activity, the brightness and how far
// equatorward the ovals sit (2 degrees per Kp, NOAA; geomag.js), and within
// OVATION_HOURS of the OVATION nowcast its probability grid sets the ovals'
// real shape and bright sectors (oval.js). Outside, the model draws as
// before: the uniforms hold the model's own series.
//
// The Sun (sunFragmentShader, src/shaders/sun.js): within REGION_DAYS of
// NOAA's region report its numbered active regions replace the modelled
// spot groups, placed by Carrington longitude on the IAU rotation and
// carried by differential rotation (sunframe.js); an M or X flare from GOES
// brightens its region while the clock is inside the flare.
//
// main.js holds three hook lines: the uniforms in each material, and one
// update call per body per frame. The UI (entry, panel, storm banner) is in
// ui.js.

import * as THREE from 'three';
import { loadSpaceWeather, refreshSpaceWeather } from './load.js';
import { kpAt } from './parse.js';
import { HARMONICS, MODEL_KP, evalSeries, modelOval, ovalUniformValues } from './oval.js';
import { heliographicScene, regionLongitude, sunPoleScene, spotModel } from './sunframe.js';

export const OVATION_HOURS = 3;
export const REGION_DAYS = 2.5;
const SUN_LIVE_MAX = 12;
const REFRESH_MS = 10 * 60 * 1000;

let data = null;
let mock = null;
const listeners = new Set();

const jdToMs = (jd) => (jd - 2440587.5) * 86400000;

// ---- data ----

export function spaceWeatherData() { return mock ? { ...data, ...mock.data } : data; }
export function onSpaceWeather(fn) { listeners.add(fn); if (data) fn(spaceWeatherData()); return () => listeners.delete(fn); }
const notify = () => { for (const fn of listeners) fn(spaceWeatherData()); };

let refreshTimer = 0;
export function startSpaceWeather() {
  if (refreshTimer) return;
  const pull = (fresh) => (fresh ? refreshSpaceWeather('all') : loadSpaceWeather('all')).then((d) => {
    if (d) { data = d; ovalCache.key = ''; notify(); }
  });
  pull(false);
  refreshTimer = setInterval(() => { if (!document.hidden) pull(true); }, REFRESH_MS);
}

// ---- aurora ----

const auroraUniforms = {
  uOval: { value: Array.from({ length: 10 }, () => new THREE.Vector4()) },
  uConeC2: { value: 0.5625 },
};
const MODEL_COEFFS = ovalUniformValues(modelOval(), modelOval());
const setOval = (values) => values.forEach((v, i) => auroraUniforms.uOval.value[i].set(v[0], v[1], v[2], v[3]));
setOval(MODEL_COEFFS);

// The aurora material's extra uniforms (spread into createEarthAuroras').
export function auroraLiveUniforms() { return auroraUniforms; }

const ovalCache = { key: '', live: null };
const auroraState = { live: false, kp: null, kind: null, shape: 'model' };
export const liveAuroraState = () => ({ ...auroraState });

// The widest an oval reaches (degrees magnetic latitude, its main arc) for
// the march's polar clip; the diffuse band runs up to 7 degrees below.
function coneFor(shapes) {
  let minLat = 90;
  for (const o of shapes) for (let i = 0; i < 16; i++) minLat = Math.min(minLat, evalSeries(o.lat, (i / 16) * 2 * Math.PI - Math.PI));
  const lat = Math.max(30, Math.min(48.59, minLat - 9));
  return Math.sin((lat * Math.PI) / 180) ** 2;
}

// Per frame from updateEarthAuroras, after the model has set its uniforms:
// u the material's uniforms, userData the shell's (its baseIntensity), jd
// the simulation's Julian date.
export function updateLiveAurora(u, userData, jd) {
  const d = spaceWeatherData();
  const ms = jdToMs(jd);
  const block = mock && mock.kp !== undefined ? { kp: mock.kp, kind: 'mock' } : d && d.aurora ? kpAt(d.aurora.kp, ms) : null;
  if (!block) {
    if (auroraState.live) { setOval(MODEL_COEFFS); auroraUniforms.uConeC2.value = 0.5625; }
    Object.assign(auroraState, { live: false, kp: null, kind: null, shape: 'model' });
    return;
  }
  const kp = block.kp;
  const ov = d && d.aurora && d.aurora.oval;
  const ovMs = d && d.aurora && d.aurora.ovation ? Date.parse(d.aurora.ovation.forecast || d.aurora.ovation.observed) : NaN;
  const useOvation = !(mock && mock.ovation === false) && ov && ov.north && ov.south && Math.abs(ms - ovMs) <= OVATION_HOURS * 3600000;
  const key = useOvation ? `ov:${ovMs}` : `kp:${kp}`;
  if (key !== ovalCache.key) {
    const north = useOvation ? ov.north : modelOval(kp);
    const south = useOvation ? ov.south : modelOval(kp);
    setOval(ovalUniformValues(north, south));
    auroraUniforms.uConeC2.value = coneFor([north, south]);
    ovalCache.key = key;
  }
  // Activity and brightness grow with Kp from the approved look's (Kp 2):
  // storms push the lower edges down (the pink hem), widen the arc system
  // and redden the upper layer, as the model already does with activity.
  u.uActivity.value = THREE.MathUtils.clamp(u.uActivity.value + 0.08 * (kp - MODEL_KP), 0.2, 1.45);
  userData.baseIntensity = (userData.baseIntensity || 0) * THREE.MathUtils.clamp(1 + 0.18 * (kp - MODEL_KP), 0.6, 2.3);
  Object.assign(auroraState, { live: true, kp, kind: block.kind, shape: useOvation ? 'ovation' : 'kp' });
}

// ---- the Sun ----

const sunUniforms = {
  uLiveSun: { value: 0 },
  // The regions' umbra core and penumbra intensity going into the shader,
  // chosen so the displayed disc (realistic gain, ACES, the eased bloom)
  // shows a big umbra at about 0.23 of the photosphere and the penumbra at
  // about 0.8, white light's contrast (measured on a 30,000 MSH test spot in
  // scripts/test-space-weather.mjs's far view; ACES's shoulder pulls a 0.82
  // penumbra up to 0.9, so it goes in at 0.68).
  uLiveTone: { value: new THREE.Vector2(0.2, 0.68) },
  uLiveCount: { value: 0 },
  uLivePole: { value: new THREE.Vector3(0, 1, 0) },
  uLiveA: { value: Array.from({ length: SUN_LIVE_MAX }, () => new THREE.Vector4()) },
  uLiveB: { value: Array.from({ length: SUN_LIVE_MAX }, () => new THREE.Vector4()) },
  uLiveC: { value: Array.from({ length: SUN_LIVE_MAX }, () => new THREE.Vector4()) },
};
// Spread into both Sun materials (buildSunBody).
export const sunLiveUniforms = sunUniforms;

const sunState = { live: false, regions: 0, flare: null };
export const liveSunState = () => ({ ...sunState });
const _q = new THREE.Quaternion();
const _v = new THREE.Vector3();
const _p = new THREE.Vector3();
const _s = new THREE.Vector3();
let lastSunMs = NaN;
let lastSunWall = 0;

// A flare's brightening at time ms: rising to its peak, then decaying past
// GOES's end time; M1 about 0.5, X1 1.0, X10 1.5.
export function flareGlow(f, ms) {
  const b = Date.parse(f.begin);
  const m = Date.parse(f.max);
  const e = Date.parse(f.end || f.max);
  if (!(ms >= b) || ms > e + 2 * (e - m + 60000)) return 0;
  const amp = THREE.MathUtils.clamp(0.5 + 0.5 * Math.log10((f.flux || 1e-5) / 1e-5), 0.3, 1.5);
  if (ms <= m) return amp * THREE.MathUtils.smoothstep(ms, b, m);
  return amp * Math.exp(-(ms - m) / Math.max(60000, (e - m) / 1.2));
}

// Per frame after the Sun has turned: sunGroup the viewer's Sun (its mesh
// draws in the group's frame), jd the simulation's Julian date.
export function updateLiveSun(sunGroup, jd) {
  const d = spaceWeatherData();
  const ms = jdToMs(jd);
  const reg = d && d.sun && d.sun.regions;
  const obsMs = reg ? Date.parse(reg.observed) : NaN;
  const live = Boolean(reg && reg.regions && reg.regions.length && Math.abs(ms - obsMs) <= REGION_DAYS * 86400000);
  sunUniforms.uLiveSun.value = live ? 1 : 0;
  sunState.live = live;
  if (!live || !sunGroup) { sunState.regions = 0; return; }
  // The regions barely move in a frame: refresh four times a second of wall
  // time, or when the clock jumps.
  const now = performance.now();
  if (Math.abs(ms - lastSunMs) < 600000 && now - lastSunWall < 250) return;
  lastSunMs = ms;
  lastSunWall = now;
  sunGroup.updateMatrixWorld();
  sunGroup.matrixWorld.decompose(_p, _q, _s);
  _q.invert();
  const pole = sunPoleScene(ms);
  sunUniforms.uLivePole.value.set(pole[0], pole[1], pole[2]).applyQuaternion(_q);
  const flares = (d.sun.flares || []).filter((f) => f.region);
  let n = 0;
  let flareNow = null;
  for (const r of reg.regions) {
    if (n >= SUN_LIVE_MAX) break;
    const L = regionLongitude(r, obsMs, ms);
    const dir = heliographicScene(r.lat, L, ms);
    _v.set(dir[0], dir[1], dir[2]).applyQuaternion(_q).normalize();
    const s = spotModel(r);
    const glow = flares.filter((f) => f.region === r.region).reduce((g, f) => Math.max(g, flareGlow(f, ms)), 0);
    if (glow > 0 && !flareNow) flareNow = r.region;
    sunUniforms.uLiveA.value[n].set(_v.x, _v.y, _v.z, s.rLead);
    sunUniforms.uLiveB.value[n].set(s.sep, s.tilt, s.penLead, s.penFoll);
    sunUniforms.uLiveC.value[n].set(s.rFoll, s.small, (r.region % 97) * 0.731, glow);
    n++;
  }
  sunUniforms.uLiveCount.value = n;
  sunState.regions = n;
  sunState.flare = flareNow;
}

// ---- checks ----

// window.__spaceWeather: the data, what is live, and a mock for captures:
// mock({ kp: 7, ovation: false }) holds Kp at 7 (and the Kp-only shape);
// mock(null) ends it.
export function installDebug() {
  if (typeof window === 'undefined') return;
  window.__spaceWeather = {
    get data() { return spaceWeatherData(); },
    aurora: liveAuroraState,
    sun: liveSunState,
    mock(m) { mock = m || null; ovalCache.key = ''; lastSunMs = NaN; notify(); return m; },
    HARMONICS,
  };
}
